import React from 'react';
import { IPageInfo } from '../../../../constants/common.interface';

export interface ITicket {
    id: number;
    ticketNumber: string;
    subject: string;
    requestedBy: string;
    createdFor: string;
    workgroup: string;
    status: string;
    priority: string;
    dateOfRequest: string;
    dueDate: string;
    taskStatus?: string;
    archiveFlag?: string;
    allowView?: boolean;
    allowEdit?: boolean;
    allowCancel?: boolean;
    allowMarkAsComplete?: boolean;
    allowClaim?: boolean;
    allowReassign?: boolean;
    allowAssign?: boolean;
    allowReopen?: boolean;
    allowRestart?: boolean;
    allowArchive?: boolean;
}

export interface ISearchCriteria {
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    pageSize: number;
}

export interface ITicketHeaderProps {
    fetchDefaultTickets: () => void;
    ticketsCount: number;
    onClaimNewTicket: (workgroup?: string) => void;
    onAddNewTicket: () => void;
    workgroupOptions: { value: string; text: string }[];
    enableTicketSupport: boolean;
    viewTicketPermission: boolean;
}

export interface ITicketTableProps {
    tickets: ITicket[];
    isAdvanceSearch: boolean;
    searchCriteria: any;
    ticketsCount: number;
    setPageSortInfo: (pageSortInfo: IPageInfo) => void;
    pageSortInfo: IPageInfo;
    onRowSelect: (selectedRows: any) => void;
    onCompleteTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCompleteVerifyTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReassignTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCancelTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReopenTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    viewTicketPermission: boolean;
}

export interface IAdvanceSearchProps {
    pageSortInfo: IPageInfo;
}
export interface IuseAdvanceSearchProps {
    pageSortInfo: IPageInfo;
}

export interface IDefaultSearchProps {
    pageSortInfo: IPageInfo;
    workgroupOptions: { value: string; text: string }[];
}

export interface IuseDefaultSearchProps {
    pageSortInfo: IPageInfo;
}

export interface IuseTicketTableProps {
    setPageSortInfo: (pageSortInfo: IPageInfo) => void;
    isAdvanceSearch: boolean;
    pageSortInfo: IPageInfo;
    onCompleteTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCompleteVerifyTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReassignTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCancelTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReopenTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    viewTicketPermission: boolean;
}

export interface ITableContext {}

export interface ICancelTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    onCancelTicketReasonOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
}
export interface IExportTicketsModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IReopenTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IErrorModal {
    errorMessage: string;
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface ICancelReasonModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IMarkVerifyCompleteModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface ICompleteTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IReassignTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
}

export interface IBulkReassignTicketModal {
    onClose: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined) => void;
    selectedRows: string[];
}

export interface IuseActionPopoverProps {
    onCompleteTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCompleteVerifyTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReassignTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onCancelTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
    onReopenTicketOpen: (
        event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> | undefined,
    ) => void;
}

export enum RequestStatus {
    SUCCESS = 'success',
    FAILURE = 'failure',
}

const filterOptionKeys = [
    'archiveFlagValue',
    'assignedToMe',
    'assignee',
    'assigneeId',
    'customFieldSelect',
    'customFieldSelectVal',
    'requestedBy',
    'requestedByName',
    'requestedBytagId',
    'requester',
    'roleId',
    'submittedAfter',
    'submittedBefore',
    'ticketCategorySelect',
    'ticketComments',
    'ticketNumber',
    'ticketPriority',
    'ticketQueues',
    'ticketStatus',
    'ticketSubject',
    'ticketType',
    'unClaimedTicket',
    'createdForRole',
    'assigneeByTag',
    'workGroupBytagId',
] as const;

// Define the interface or type dynamically
export type IFilterOptions = Record<(typeof filterOptionKeys)[number], boolean>;
